import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { completeModule, getLearningPath } from '../services/firebase';
import { saveDeckCards, loadDeckCards } from '../services/spacedRepetition';
import { API_URL } from '../config';
import './LessonComplete.css';

function LessonComplete() {
    const [showConfetti, setShowConfetti] = useState(true);
    const [confettiPieces, setConfettiPieces] = useState([]);
    const [moduleSaved, setModuleSaved] = useState(false);
    const [flashcardsGenerated, setFlashcardsGenerated] = useState(false);
    const [generatingCards, setGeneratingCards] = useState(false);
    const [cardsCount, setCardsCount] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, refreshUserData } = useAuth();

    const {
        xpEarned,
        topic,
        moduleId,
        maxStreak,
        fromWorldMap,
        lessonTitle,
        lessonContent,
        lessonQuestions
    } = location.state || { maxStreak: 0 };

    useEffect(() => {
        // Mark module as complete in Firebase
        const saveModuleCompletion = async () => {
            if (currentUser && topic && moduleId && !moduleSaved) {
                const result = await completeModule(currentUser.uid, topic, moduleId);
                if (result.success) {
                    console.log('✅ Module marked as complete!');
                    // Refresh user data to show updated completed modules
                    await refreshUserData();
                    setModuleSaved(true);

                    // Generate flashcards for this level
                    generateFlashcardsForLevel();
                }
            }
        };

        saveModuleCompletion();

        // Generate confetti
        const pieces = [];
        for (let i = 0; i < 50; i++) {
            pieces.push({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 0.5,
                duration: 2 + Math.random() * 2,
                color: ['#667eea', '#764ba2', '#57F287', '#FEE75C', '#ED4245'][Math.floor(Math.random() * 5)]
            });
        }
        setConfettiPieces(pieces);

        setTimeout(() => setShowConfetti(false), 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateFlashcardsForLevel = async () => {
        if (!currentUser || !topic || !moduleId || flashcardsGenerated || generatingCards) return;

        // Get the level number from the module position
        const pathResult = await getLearningPath(currentUser.uid, topic);
        if (!pathResult.success || !pathResult.data?.modules) return;

        const modules = pathResult.data.modules;
        const levelIndex = modules.findIndex(m => m.id === moduleId);
        const levelNum = levelIndex + 1;

        if (levelNum <= 0) return;

        // Check if cards already exist for this level
        const existingCards = loadDeckCards(currentUser.uid, topic, levelNum);
        if (existingCards && existingCards.length > 0) {
            console.log(`📇 Flashcards already exist for Level ${levelNum}`);
            setCardsCount(existingCards.length);
            setFlashcardsGenerated(true);
            return;
        }

        setGeneratingCards(true);

        try {
            console.log(`🎴 Generating flashcards for Level ${levelNum}: ${lessonTitle || moduleId}`);

            // Build context from lesson content
            const moduleContext = [{
                title: lessonTitle || modules[levelIndex]?.title || moduleId,
                concepts: modules[levelIndex]?.concepts || [],
                description: modules[levelIndex]?.description || ''
            }];

            // Add teaching content if available
            if (lessonContent && lessonContent.length > 0) {
                moduleContext[0].teachingContent = lessonContent.map(t => t.content).join('\n\n');
            }

            // Add questions as hints for card generation
            if (lessonQuestions && lessonQuestions.length > 0) {
                moduleContext[0].questionsAsked = lessonQuestions.map(q => ({
                    question: q.question,
                    correctAnswer: q.correctAnswer
                }));
            }

            const response = await fetch(`${API_URL}/api/generate-flashcards`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topic: topic,
                    count: 10, // 10 cards per level
                    moduleContext: moduleContext,
                    levelSpecific: true
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate flashcards');
            }

            const data = await response.json();

            // Initialize cards with spaced repetition data
            const initializedCards = data.map((card, index) => ({
                ...card,
                id: `${topic}_L${levelNum}_${index}_${Date.now()}`,
                level: levelNum,
                moduleId: moduleId,
                easinessFactor: 2.5,
                repetitions: 0,
                interval: 0,
                nextReviewDate: null,
                lastReviewedDate: null,
                totalReviews: 0
            }));

            // Save to deck storage
            await saveDeckCards(currentUser.uid, topic, levelNum, initializedCards);

            console.log(`✅ Generated ${initializedCards.length} flashcards for Level ${levelNum}`);
            setCardsCount(initializedCards.length);
            setFlashcardsGenerated(true);
        } catch (error) {
            console.error('Error generating flashcards:', error);
        } finally {
            setGeneratingCards(false);
        }
    };

    return (
        <div className="lesson-complete-container">
            {/* Confetti */}
            {showConfetti && confettiPieces.map(piece => (
                <div
                    key={piece.id}
                    className="confetti-piece"
                    style={{
                        left: `${piece.left}%`,
                        animationDelay: `${piece.delay}s`,
                        animationDuration: `${piece.duration}s`,
                        background: piece.color
                    }}
                />
            ))}

            {/* Main Content */}
            <div className="complete-content animate-pop-in">
                <div className="trophy-icon">🏆</div>
                <h1 className="complete-title">Lesson Complete!</h1>
                <p className="complete-subtitle">{topic}</p>

                {/* Stats Cards */}
                <div className="stats-row">
                    <div className="stat-card xp-card">
                        <div className="stat-icon">💎</div>
                        <div className="stat-value">{xpEarned}</div>
                        <div className="stat-label">XP Earned</div>
                    </div>

                    {maxStreak > 0 && (
                        <div className="stat-card streak-card">
                            <div className="stat-icon">🔥</div>
                            <div className="stat-value">{maxStreak}</div>
                            <div className="stat-label">Max Streak</div>
                        </div>
                    )}

                    {/* Flashcards Generated Indicator */}
                    {(generatingCards || flashcardsGenerated) && (
                        <div className="stat-card flashcard-card">
                            <div className="stat-icon">📇</div>
                            <div className="stat-value">
                                {generatingCards ? '...' : cardsCount}
                            </div>
                            <div className="stat-label">
                                {generatingCards ? 'Creating Cards' : 'New Cards'}
                            </div>
                        </div>
                    )}
                </div>

                {/* Achievements */}
                <div className="achievements-earned">
                    <h3>Achievements Unlocked</h3>
                    <div className="achievement-badges">
                        {xpEarned >= 50 && (
                            <div className="achievement-badge">
                                <span className="badge-icon">⭐</span>
                                <span className="badge-name">Quick Learner</span>
                            </div>
                        )}
                        {maxStreak >= 3 && (
                            <div className="achievement-badge">
                                <span className="badge-icon">🔥</span>
                                <span className="badge-name">On Fire!</span>
                            </div>
                        )}
                        {maxStreak >= 5 && (
                            <div className="achievement-badge">
                                <span className="badge-icon">💯</span>
                                <span className="badge-name">Perfectionist</span>
                            </div>
                        )}
                        {flashcardsGenerated && (
                            <div className="achievement-badge">
                                <span className="badge-icon">📇</span>
                                <span className="badge-name">Cards Unlocked!</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <button
                        className="btn btn-primary btn-large"
                        onClick={() => fromWorldMap
                            ? navigate('/world-map', { state: { topic } })
                            : navigate('/learning-path', { state: { topic } })
                        }
                    >
                        {fromWorldMap ? '🗺️ Back to World Map' : 'Continue Learning Path →'}
                    </button>

                    {flashcardsGenerated && (
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate('/flashcard-decks', { state: { topic } })}
                        >
                            📇 Study Flashcards
                        </button>
                    )}

                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate('/progress')}
                    >
                        View Progress
                    </button>
                </div>

                {/* Motivational Message */}
                <div className="motivational-message">
                    <p>🎯 Keep going! You're making amazing progress.</p>
                    {flashcardsGenerated && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                            📇 {cardsCount} flashcards have been added to your study deck!
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default LessonComplete;
